import { FunctionComponent, useContext, useState } from "react";
import { useAppSelector } from "./store/hooks";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import { Player } from "./Types";
import { MatchScore } from "./store/matchScoreSlice";
import { GameState } from "./store/gameStateSlice";
import { isCurrentPlayer } from "./Utils";

/**
 * Варианты сдачи:
 * Оин        — обычная сдача,             ×1 очко
 * Марс       — гаммон (не вывел шашки),   ×2 очка
 * Кокс       — бэкгаммон,                 ×3 очка
 * Домашний марс — марс в домашнем поле,   ×4 очка (вариант турнирных правил)
 */
const FORFEIT_OPTIONS = [
  { label: "Оин",          multiplier: 1, desc: "Обычная сдача — 1 очко" },
  { label: "Марс",         multiplier: 2, desc: "Гаммон — 2 очка" },
  { label: "Кокс",         multiplier: 3, desc: "Бэкгаммон — 3 очка" },
  { label: "Домашний марс",multiplier: 4, desc: "Редкий вариант — 4 очка" },
] as const;

const ForfeitMenu: FunctionComponent = () => {
  const [open, setOpen] = useState(false);

  const [gameState, players, currentPlayer, doublingCubeData, gameBoardState, matchScore] =
    useAppSelector((state) => [
      state.gameState,
      state.players,
      state.currentPlayer,
      state.doublingCube,
      state.gameBoard,
      state.matchScore,
    ]);
  const actions = useContext(ActionsContext);

  // Кнопка видна только в активной фазе игры
  const isActive =
    gameState === GameState.PlayerMoving ||
    gameState === GameState.PlayerRolling;

  if (!isActive) return null;

  // Сдаться можно только в свой ход.
  // Локальная игра = игра с ботом, игрок = Player.One.
  const canForfeit = isCurrentPlayer(players, currentPlayer, actions);

  if (!canForfeit) return null;

  // Сдаётся currentPlayer (тот, чей ход) — он проигрывает.
  // Победитель = противник currentPlayer.
  const handleForfeit = async (multiplier: number) => {
    setOpen(false);
    const stake = (doublingCubeData.enabled ? doublingCubeData.gameStakes : 1) * multiplier;
    const winner: Player = currentPlayer === Player.One ? Player.Two : Player.One;
    const newScore: MatchScore = {
      [Player.One]: matchScore[Player.One] + (winner === Player.One ? stake : 0),
      [Player.Two]: matchScore[Player.Two] + (winner === Player.Two ? stake : 0),
      pointsRequiredToWin: matchScore.pointsRequiredToWin,
    };
    await actions.forfeitButtonClicked(gameBoardState, newScore);
  };

  return (
    <div className="Forfeit-container">
      <button
        className="Forfeit-trigger"
        onClick={() => setOpen((v) => !v)}
        title="Сдаться"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Флагшток */}
          <line x1="3" y1="1" x2="3" y2="15" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          {/* Флаг */}
          <path d="M3 2.5 L13.5 5.5 L3 8.5 Z" fill="white"/>
        </svg>
      </button>

      {open && (
        <div className="Forfeit-dropdown">
          <div className="Forfeit-dropdown-title">Выберите вариант</div>
          {FORFEIT_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              className="Forfeit-option"
              onClick={() => handleForfeit(opt.multiplier)}
            >
              <span className="Forfeit-option-label">{opt.label}</span>
              <span className="Forfeit-option-desc">{opt.desc}</span>
            </button>
          ))}
          <button className="Forfeit-cancel" onClick={() => setOpen(false)}>
            Отмена
          </button>
        </div>
      )}

      {/* Затемнение фона при открытом меню */}
      {open && (
        <div className="Forfeit-backdrop" onClick={() => setOpen(false)} />
      )}
    </div>
  );
};

export default ForfeitMenu;
